import React, { useState, useEffect, useRef } from 'react';
import { Receipt, Layers, Gift } from 'lucide-react';
import { costoActividadService } from '../services/costoActividadService';
import { productoActividadService } from '../services/productoActividadService';
import { produccionService } from '../services/produccionService';
import { CostoActividad, ProductoActividad, Produccion } from '../lib/supabase';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { MoneyInput } from './ui/MoneyInput';
import { TextInput } from './ui/TextInput';
import { Select } from './ui/Select';
import { DialogShell } from './ui/DialogShell';
import { ModalHeader } from './ui/ModalHeader';
import { FORM_BORDER, FORM_BORDER_HOVER, FOCUS_RING, FORM_RADIUS } from './ui/tokens';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { ComprobanteInput } from './comprobantes/ComprobanteInput';
import { MedioPagoFolio } from './comprobantes/MedioPagoFolio';
import { useComprobanteFormulario } from '../hooks/useComprobanteFormulario';
import { comprobanteService } from '../services/comprobanteService';
import { mensajeErrorDeNegocio } from '../utils/erroresDeNegocio';
import type { EntidadDinero } from '../types/comprobante';
import { obtenerCambiosCostoActividad, type DatosEdicionCostoActividad } from '../utils/edicionCostoActividad';
import { validarImporteCostoActividad } from '../utils/validacionImportesActividad';
import { fechaEnChile } from '../utils/dateHelpers';

interface CostoActividadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  actividadId: string;
  costo?: CostoActividad;
}

function claseDelReembolso(esDonacion: boolean, requiereReembolso: boolean): string {
  if (esDonacion) return 'border-slate-200 bg-slate-100 opacity-50 cursor-not-allowed';
  if (requiereReembolso) return 'border-amber-400 bg-amber-50 cursor-pointer';
  return 'border-slate-200 bg-slate-50 hover:border-slate-300 cursor-pointer';
}

function etiquetaDeGuardar(guardando: boolean, esEdicion: boolean): string {
  if (guardando) return 'Guardando...';
  return esEdicion ? 'Actualizar' : 'Registrar Gasto';
}

export const CostoActividadModal: React.FC<CostoActividadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  actividadId,
  costo
}) => {
  const [loading, setLoading] = useState(false);
  const [productos, setProductos] = useState<ProductoActividad[]>([]);
  const [producciones, setProducciones] = useState<Produccion[]>([]);

  // Hook centralizado para manejo de errores
  const { error, clearError, handleError } = useErrorHandler();

  // Entidad dueña de la actividad: es donde queda guardado el comprobante. Solo hace falta al registrar.
  const [entidad, setEntidad] = useState<EntidadDinero | null>(null);

  // Al cambiar de actividad con el modal montado, lo cargado de la anterior no puede quedar a la vista:
  // se descarta en el render y las cargas de la actividad nueva lo reemplazan.
  const [actividadCargada, setActividadCargada] = useState(actividadId);
  if (actividadCargada !== actividadId) {
    setActividadCargada(actividadId);
    setProductos([]);
    setProducciones([]);
    setEntidad(null);
  }

  useEffect(() => {
    if (!isOpen || costo) return;
    let vigente = true;
    comprobanteService.getEntidadDeActividad(actividadId)
      .then((dueña) => { if (vigente) setEntidad(dueña); })
      .catch((err: unknown) => { if (vigente) reportarErrorInesperado(err, 'No pudimos preparar el comprobante del gasto.'); });
    return () => { vigente = false; };
  }, [isOpen, costo, actividadId]);

  const [formData, setFormData] = useState({
    producto_id: '',
    produccion_id: '',
    descripcion: '',
    monto: 0,
    fecha: fechaEnChile(new Date()) ?? '',
    tipo_costo: 'materia_prima' as 'materia_prima' | 'producto' | 'otro',
    requiere_reembolso: false,
    es_donacion: false
  });

  // Una donación no sale de la caja: el comprobante y el medio de pago son opcionales.
  const comprobante = useComprobanteFormulario({
    entidad,
    fechaMovimiento: formData.fecha,
    permiteSubirDespues: true,
    pideMedioPago: !formData.es_donacion,
    sentidoDinero: 'egreso',
    obligatoriedad: formData.es_donacion ? 'nunca' : 'desde_vigencia',
  });

  // Función para detectar si hay cambios sin guardar
  const hasFormChanges = () => {
    if (!costo) {
      // Si es un nuevo costo, verificar si hay datos en el formulario
      return formData.descripcion !== '' || formData.monto > 0 || formData.producto_id !== '' || comprobante.hayCambios;
    }
    // Si es edición, verificar si los datos cambiaron
    return (
      formData.descripcion !== costo.descripcion ||
      formData.monto !== Number(costo.monto) ||
      formData.producto_id !== (costo.producto_id || '') ||
      formData.fecha !== costo.fecha.split('T')[0] ||
      formData.tipo_costo !== costo.tipo_costo ||
      formData.requiere_reembolso !== (costo.requiere_reembolso ?? false) ||
      formData.es_donacion !== (costo.es_donacion ?? false)
    );
  };

  const isFormDirty = () => loading || error !== '' || hasFormChanges();

  // Numera las cargas de lotes: solo la última pedida puede publicar su resultado, así una respuesta
  // tardía de otro producto (o de una apertura anterior) no reemplaza la lista vigente.
  const cargaProduccionesRef = useRef(0);

  const loadProductos = async (vigente: () => boolean) => {
    try {
      const data = await productoActividadService.getByActividad(actividadId);
      if (vigente()) setProductos(data);
    } catch (err) {
      if (vigente()) reportarErrorInesperado(err, 'No pudimos cargar los productos.');
    }
  };

  const loadProducciones = async (productoId: string) => {
    cargaProduccionesRef.current += 1;
    const carga = cargaProduccionesRef.current;
    try {
      const data = await produccionService.getByProducto(productoId);
      if (carga === cargaProduccionesRef.current) setProducciones(data);
    } catch (err) {
      if (carga !== cargaProduccionesRef.current) return;
      reportarErrorInesperado(err, 'No pudimos cargar los lotes de producción.');
      setProducciones([]);
    }
  };

  useEffect(() => {
    let vigente = true;
    if (isOpen) {
      void loadProductos(() => vigente);
    }
    if (costo) {
      setFormData({
        producto_id: costo.producto_id || '',
        produccion_id: costo.produccion_id || '',
        descripcion: costo.descripcion,
        monto: Number(costo.monto),
        fecha: costo.fecha.split('T')[0],
        tipo_costo: costo.tipo_costo,
        requiere_reembolso: costo.requiere_reembolso ?? false,
        es_donacion: costo.es_donacion ?? false
      });
      // Si hay producto_id, cargar sus producciones
      if (costo.producto_id) {
        setProducciones([]);
        loadProducciones(costo.producto_id);
      }
    } else {
      setFormData({
        producto_id: '',
        produccion_id: '',
        descripcion: '',
        monto: 0,
        fecha: fechaEnChile(new Date()) ?? '',
        tipo_costo: 'materia_prima',
        requiere_reembolso: false,
        es_donacion: false
      });
      setProducciones([]);
    }
    handleError('');
    return () => {
      vigente = false;
      cargaProduccionesRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- carga y reseteo controlados por costo/isOpen/actividadId
  }, [costo, isOpen, actividadId]);

  // Manejar cambio de producto
  const handleProductoChange = (productoId: string) => {
    setFormData({ ...formData, producto_id: productoId, produccion_id: '' });
    // Los lotes del producto anterior no se ofrecen mientras llegan los del nuevo
    setProducciones([]);
    if (productoId) {
      loadProducciones(productoId);
    } else {
      cargaProduccionesRef.current += 1;
      setProducciones([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errorImporte = validarImporteCostoActividad(formData.monto, formData.es_donacion);
    if (errorImporte) {
      handleError(errorImporte);
      return;
    }

    if (!costo) {
      const errorComprobante = comprobante.validar();
      if (errorComprobante) {
        handleError(errorComprobante);
        return;
      }
    }

    handleError('');
    setLoading(true);

    try {
      const dataToSave: DatosEdicionCostoActividad = {
        ...formData,
        producto_id: formData.producto_id || null,
        produccion_id: formData.produccion_id || null
      };

      if (costo) {
        await costoActividadService.update(costo.id, obtenerCambiosCostoActividad(costo, dataToSave));
      } else {
        const respaldo = await comprobante.resolver();
        await costoActividadService.create({
          actividad_id: actividadId,
          ...dataToSave
        }, respaldo);
        comprobante.reiniciar();
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      const deNegocio = mensajeErrorDeNegocio(err);
      if (deNegocio) handleError(deNegocio);
      else reportarErrorInesperado(err, 'No pudimos guardar el gasto.');
    } finally {
      setLoading(false);
    }
  };

  const handleDonacionToggle = () => {
    // "Subir después" no existe para una donación: si estaba elegido, se descarta al marcarla.
    if (!formData.es_donacion && comprobante.seleccion.tipo === 'despues') comprobante.cambiarSeleccion({ tipo: 'ninguno' });
    setFormData({
      ...formData,
      es_donacion: !formData.es_donacion,
      requiere_reembolso: formData.es_donacion ? formData.requiere_reembolso : false
    });
  };

  const reembolsoClassName = claseDelReembolso(formData.es_donacion, formData.requiere_reembolso);

  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  if (!isOpen) return null;

  const submitLabel = etiquetaDeGuardar(loading, costo !== undefined);

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      hasUnsavedChanges={isFormDirty()}
      closeRef={closeRef}
      header={(
        <ModalHeader
          variant="gradient-red"
          title={costo ? 'Editar Gasto' : 'Registrar Gasto'}
          subtitle={costo ? 'Actualiza la informacion' : 'Registra un nuevo gasto de la actividad'}
          icon={<Receipt className="w-6 h-6" />}
          onClose={requestClose}
        />
      )}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
          <FormErrorAlert error={error} onClear={clearError} />

          {!costo && (
            <ComprobanteInput comprobante={comprobante} deshabilitado={loading} />
          )}

          {productos.length > 0 && (
            <Select
              label="Producto"
              options={productos.map((p) => ({ value: p.id, label: p.nombre_producto }))}
              value={formData.producto_id}
              onChange={handleProductoChange}
              placeholder="Gasto general (se distribuye entre todos los productos)"
            />
          )}

          {/* Selector de Lote - solo si hay producto seleccionado y lotes */}
          {formData.producto_id && producciones.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Layers className="w-4 h-4 text-purple-800" />
                <span className="text-sm font-semibold text-purple-800">Lote</span>
              </div>
              <Select
                options={producciones.map((p) => ({
                  value: p.id,
                  label: `${p.nombre} - Stock: ${p.stock_inicial} uds`,
                }))}
                value={formData.produccion_id}
                onChange={(v) => setFormData({ ...formData, produccion_id: v })}
                placeholder="Gasto general del producto (todos los lotes)"
              />
            </div>
          )}

          <TextInput
            label="Descripción"
            value={formData.descripcion}
            onChange={(v) => setFormData({ ...formData, descripcion: v })}
            placeholder="Ej: Compra de ingredientes, Arriendo local, etc."
            required
          />

          <Select
            label="Tipo de Gasto"
            options={[
              { value: 'materia_prima', label: 'Materia Prima' },
              { value: 'producto', label: 'Producto' },
              { value: 'otro', label: 'Otro' },
            ]}
            value={formData.tipo_costo}
            onChange={(v) => setFormData({ ...formData, tipo_costo: v as 'materia_prima' | 'producto' | 'otro' })}
            required
          />

          <div>
            <MoneyInput
              label={formData.es_donacion ? 'Valor estimado de la donación' : 'Monto'}
              required={!formData.es_donacion}
              value={formData.monto}
              onChange={(val) => setFormData({ ...formData, monto: val })}
              min={0}
            />
          </div>

          <div>
            <label htmlFor="costo-fecha" className="block text-sm font-medium text-slate-600 mb-1.5">
              Fecha <span className="text-red-500">*</span>
            </label>
            <input
              id="costo-fecha"
              type="date"
              required
              value={formData.fecha}
              onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
              className={`w-full px-4 py-3 ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
            />
          </div>

          {!costo && <MedioPagoFolio comprobante={comprobante} deshabilitado={loading} />}

          {/* Donación y reembolso en una sola fila */}
          <div className="flex gap-3">
            <div
              role="button"
              tabIndex={0}
              onClick={handleDonacionToggle}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleDonacionToggle(); }}
              className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                formData.es_donacion
                  ? 'border-emerald-400 bg-emerald-50'
                  : 'border-slate-200 bg-slate-50 hover:border-slate-300'
              }`}
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                formData.es_donacion ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 bg-white'
              }`}>
                {formData.es_donacion && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Gift className={`w-4 h-4 flex-shrink-0 ${formData.es_donacion ? 'text-emerald-600' : 'text-slate-400'}`} />
                <span className={`text-sm font-semibold ${formData.es_donacion ? 'text-emerald-800' : 'text-slate-700'}`}>
                  Es una donación
                </span>
              </div>
            </div>

            <div
              role="button"
              tabIndex={0}
              onClick={() => {
                if (!formData.es_donacion) {
                  setFormData({ ...formData, requiere_reembolso: !formData.requiere_reembolso });
                }
              }}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !formData.es_donacion) {
                  setFormData({ ...formData, requiere_reembolso: !formData.requiere_reembolso });
                }
              }}
              className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${reembolsoClassName}`}
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                formData.requiere_reembolso && !formData.es_donacion ? 'border-amber-500 bg-amber-500' : 'border-slate-300 bg-white'
              }`}>
                {formData.requiere_reembolso && !formData.es_donacion && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className={`text-sm font-semibold ${
                formData.requiere_reembolso && !formData.es_donacion ? 'text-amber-800' : 'text-slate-700'
              }`}>Pendiente de reembolso</span>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={requestClose}
              className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || comprobante.subiendo}
              className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitLabel}
            </button>
          </div>
      </form>
    </DialogShell>
  );
};
